import { useMemo } from "react";
import { MultiMetricPicker } from "@/components/MetricPicker";
import { fmtMetricValue, getMetric } from "@/lib/metrics";
import { initialsFor, parseNumberOrZero, tintFor } from "@/lib/overview-helpers";
import type { App, ByAppRow } from "@/lib/types";
import { CustomizeButton } from "./CustomizeButton";

export function ByApplicationCard({
  apps,
  byApp,
  loading,
  metricIds,
  onMetricsChange,
}: {
  apps: App[];
  byApp: ByAppRow[];
  loading: boolean;
  metricIds: string[];
  onMetricsChange: (ids: string[]) => void;
}) {
  const rows = useMemo(() => {
    const byAppMap = new Map<number, ByAppRow>();
    for (const r of byApp) byAppMap.set(r.app_id, r);
    const list = apps.map((a) => {
      const rollup = byAppMap.get(a.id);
      const values = metricIds.map((id) =>
        rollup ? getMetric(id).fromByApp(rollup) : null
      );
      const spendTotal = rollup ? parseNumberOrZero(rollup.spend_usd) : 0;
      return { ...a, values, spendTotal };
    });
    const totalSpend = list.reduce((s, r) => s + r.spendTotal, 0) || 1;
    return list
      .map((r) => ({ ...r, share: r.spendTotal / totalSpend }))
      .sort((a, b) => b.spendTotal - a.spendTotal);
  }, [apps, byApp, metricIds]);

  const metricLabels = metricIds.map((id) => getMetric(id).short).join(" / ");

  return (
    // Pencil moved into the header as a real, always-visible button —
    // hover-reveal was too easy to miss, users didn't realize the
    // metrics were configurable.
    <div className="card">
      <div className="card-head" style={{ alignItems: "center" }}>
        <h3>By application</h3>
        <div
          style={{
            marginLeft: "auto",
            display: "inline-flex",
            alignItems: "center",
            gap: 8,
          }}
        >
          <span className="muted">{metricLabels}</span>
          <MultiMetricPicker
            values={metricIds}
            onChange={onMetricsChange}
            min={1}
            max={4}
            title="Columns on this widget"
            trigger={({ toggle, open }) => (
              <CustomizeButton active={open} onClick={toggle} />
            )}
          />
        </div>
      </div>
      <div style={{ padding: "4px 0 8px" }}>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: `1fr repeat(${metricIds.length}, minmax(72px, auto))`,
            padding: "6px 16px 4px",
            gap: 14,
            fontSize: 10.5,
            color: "var(--c-text-4)",
            textTransform: "uppercase",
            letterSpacing: 0.4,
            borderBottom: "1px solid var(--c-divider)",
          }}
        >
          <div>Application</div>
          {metricIds.map((id) => (
            <div key={id} style={{ textAlign: "right" }}>
              {getMetric(id).short}
            </div>
          ))}
        </div>
        {loading && rows.every((r) => r.values.every((v) => v == null || v === 0)) && (
          <div
            className="muted"
            style={{
              padding: "20px 16px",
              textAlign: "center",
              fontSize: 13,
            }}
          >
            Loading…
          </div>
        )}
        {!loading && rows.length === 0 && (
          <div
            className="muted"
            style={{
              padding: "20px 16px",
              textAlign: "center",
              fontSize: 13,
            }}
          >
            No campaigns in this period.
          </div>
        )}
        {rows.map((r, i) => (
          <div
            key={r.id}
            style={{
              display: "grid",
              gridTemplateColumns: `1fr repeat(${metricIds.length}, minmax(72px, auto))`,
              alignItems: "center",
              padding: "10px 16px",
              gap: 14,
              borderBottom:
                i < rows.length - 1 ? "1px solid var(--c-divider)" : "0",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                minWidth: 0,
              }}
            >
              <div
                style={{
                  width: 24,
                  height: 24,
                  borderRadius: 6,
                  background: tintFor(r.id),
                  color: "#fff",
                  fontSize: 10,
                  fontWeight: 600,
                  display: "grid",
                  placeItems: "center",
                  flex: "none",
                }}
              >
                {initialsFor(r.name)}
              </div>
              <div style={{ minWidth: 0 }}>
                <div
                  style={{
                    fontWeight: 500,
                    fontSize: 13,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {r.name}
                </div>
                <div className="bar-track" style={{ marginTop: 4 }}>
                  <div
                    className="bar-fill"
                    style={{ right: `${(1 - r.share) * 100}%` }}
                  />
                </div>
              </div>
            </div>
            {metricIds.map((id, col) => (
              <div
                key={id}
                className="mono"
                style={{ textAlign: "right", fontSize: 12.5 }}
              >
                {fmtMetricValue(id, r.values[col] ?? null)}
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}
